import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SUBMIT_LABEL = { login: "Accedi", register: "Crea il tuo account" };

const Auth = () => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", invite_code: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(endpoint, payload);
      setUser(data);
      toast.success(mode === "login" ? `Bentornato, ${data.name}!` : `Benvenuto nella famiglia, ${data.name}!`);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#E8DFD5] bg-[#FBF8F3] px-4 py-3.5 text-sm text-[#2C2723] outline-none transition-all duration-300 placeholder:text-[#8C7B6E]/60 focus:border-[#B85328] focus:ring-2 focus:ring-[#B85328]/15";

  return (
    <main className="mx-auto flex max-w-md flex-col px-4 py-16 sm:py-24">
      <div className="text-center">
        <p className="eyebrow justify-center">La community della Casetta</p>
        <h1 data-testid="auth-heading" className="font-display mt-3 text-3xl font-semibold text-[#2C2723] sm:text-4xl">
          {mode === "login" ? (
            <>Bentornato <span className="italic text-[#B85328]">in famiglia</span></>
          ) : (
            <>Entra <span className="italic text-[#B85328]">nella famiglia</span></>
          )}
        </h1>
        <p className="mt-3 text-sm text-[#8C7B6E]">
          Riservato alle famiglie che hanno accolto un bassotto della Casetta.
        </p>
      </div>

      <div className="mt-8 rounded-3xl border border-[#E8DFD5] bg-white p-7 shadow-sm sm:p-9">
        <div className="mb-6 grid grid-cols-2 rounded-full bg-[#F5EFE6] p-1">
          {[
            ["login", "Accedi"],
            ["register", "Registrati"],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`auth-tab-${key}`}
              onClick={() => { setMode(key); setError(""); }}
              className={`rounded-full py-2.5 text-sm font-semibold transition-all duration-300 ${
                mode === key ? "bg-[#1F1A17] text-[#FBF8F3]" : "text-[#5C534B] hover:text-[#2C2723]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <button
          data-testid="auth-google-button"
          onClick={googleLogin}
          className="flex w-full items-center justify-center gap-3 rounded-full border border-[#CFC0B2] py-3.5 text-sm font-semibold text-[#2C2723] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#B85328] hover:shadow-md"
        >
          <svg width="17" height="17" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
          </svg>
          Continua con Google
        </button>

        <div className="my-6 flex items-center gap-4 text-xs text-[#8C7B6E]">
          <span className="h-px flex-1 bg-[#E8DFD5]" />
          oppure con email
          <span className="h-px flex-1 bg-[#E8DFD5]" />
        </div>

        <form data-testid="auth-form" onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input
              data-testid="auth-input-name"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Il tuo nome"
              className={inputCls}
            />
          )}
          {mode === "register" && (
            <input
              data-testid="auth-input-invite-code"
              required
              value={form.invite_code}
              onChange={set("invite_code")}
              placeholder="Codice invito (es. CASETTA-AB12CD)"
              className={`${inputCls} font-mono tracking-wider uppercase`}
            />
          )}
          <input
            data-testid="auth-input-email"
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="Email"
            className={inputCls}
          />
          <input
            data-testid="auth-input-password"
            type="password"
            required
            minLength={8}
            value={form.password}
            onChange={set("password")}
            placeholder="Password (minimo 8 caratteri)"
            className={inputCls}
          />
          {mode === "login" && (
            <div className="text-right">
              <Link
                to="/recupera"
                data-testid="auth-forgot-password-link"
                className="text-xs font-semibold text-[#8C7B6E] underline-offset-4 transition-colors hover:text-[#B85328] hover:underline"
              >
                Password dimenticata?
              </Link>
            </div>
          )}
          {error && (
            <p data-testid="auth-error-message" className="rounded-xl bg-[#B85328]/10 px-4 py-3 text-xs font-medium text-[#B85328]">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="auth-submit-button"
            disabled={busy}
            className="w-full rounded-full bg-[#B85328] py-4 text-sm font-semibold text-[#FBF8F3] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#9C421C] hover:shadow-xl disabled:opacity-60"
          >
            {busy ? "Un attimo..." : SUBMIT_LABEL[mode]}
          </button>
        </form>
        {mode === "register" && (
          <p className="mt-5 text-center text-xs leading-relaxed text-[#8C7B6E]">
            L'iscrizione è riservata alle famiglie adottive. Non hai un codice invito?{" "}
            <Link to="/iscriviti" data-testid="auth-invite-info-link" className="font-semibold text-[#B85328] underline underline-offset-4">
              Scopri come richiederlo
            </Link>
          </p>
        )}
      </div>
    </main>
  );
};

export default Auth;
